import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import {
  CircleCheck, CircleAlert, CircleX, CircleHelp,
  RefreshCw, Search, Settings2, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pagination } from '@/components/ui/pagination';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  useMyDataSyncs,
  useMyDataSummary,
  useMyDataCredentials,
  useSetMyDataCredentials,
  useTestMyDataConnection,
  useTriggerMyDataSync,
} from '@/hooks/queries';

// ─── Status helpers ──────────────────────────────────────

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'GREEN':
      return <CircleCheck className="h-4 w-4 text-green-500" />;
    case 'YELLOW':
      return <CircleAlert className="h-4 w-4 text-amber-500" />;
    case 'RED':
      return <CircleX className="h-4 w-4 text-red-500" />;
    default:
      return <CircleHelp className="h-4 w-4 text-gray-400" />;
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'GREEN': return 'OK';
    case 'YELLOW': return 'Εκκρεμεί';
    case 'RED': return 'Πρόβλημα';
    default: return 'Άγνωστο';
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'GREEN': return 'status-ok';
    case 'YELLOW': return 'status-review';
    case 'RED': return 'status-danger';
    default: return 'bg-secondary text-muted-foreground';
  }
}

function statusVariant(status: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  switch (status) {
    case 'GREEN': return 'success';
    case 'YELLOW': return 'warning';
    case 'RED': return 'destructive';
    default: return 'secondary';
  }
}

// ─── Main Page ───────────────────────────────────────────

export function MyDataPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [credDialogOpen, setCredDialogOpen] = useState(false);

  const { data: summary, isLoading: summaryLoading } = useMyDataSummary();
  const { data: credentials } = useMyDataCredentials();
  const { data: syncsData, isLoading: syncsLoading } = useMyDataSyncs({
    page,
    limit,
    search: search || undefined,
    status: statusFilter || undefined,
    sortBy: 'clientBusinessName',
    sortOrder: 'asc',
  });
  const triggerSync = useTriggerMyDataSync();

  const syncs = syncsData?.data ?? [];
  const total = syncsData?.total ?? 0;
  const totalPages = syncsData?.totalPages ?? 1;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-display">myDATA</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Κατάσταση πελατών — ΑΑΔΕ myDATA
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setCredDialogOpen(true)}>
          <Settings2 className="h-4 w-4 mr-1.5" />
          Διαπιστευτήρια
        </Button>
      </div>

      {/* Not configured banner */}
      {credentials && !credentials.configured && (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="p-4 flex items-center gap-3">
            <CircleAlert className="h-5 w-5 text-warning flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">
                Τα διαπιστευτήρια myDATA δεν έχουν ρυθμιστεί
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Ρυθμίστε το User ID και Subscription Key από τις ρυθμίσεις για να ενεργοποιήσετε τον συγχρονισμό.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setCredDialogOpen(true)}>
              Ρύθμιση
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Πελάτες OK"
          value={summaryLoading ? '...' : String(summary?.greenCount ?? 0)}
          color="text-green-600"
          icon={CircleCheck}
        />
        <SummaryCard
          label="Εκκρεμείς χαρακτηρισμοί"
          value={summaryLoading ? '...' : String(summary?.totalPendingClassification ?? 0)}
          color="text-amber-600"
          icon={CircleAlert}
        />
        <SummaryCard
          label="Απορρίψεις"
          value={summaryLoading ? '...' : String(summary?.totalRejected ?? 0)}
          color="text-red-600"
          icon={CircleX}
        />
        <SummaryCard
          label="Κενά αρίθμησης"
          value={summaryLoading ? '...' : String(summary?.totalGaps ?? 0)}
          color="text-red-600"
          icon={CircleX}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Αναζήτηση πελάτη..."
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'ALL' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Κατάσταση" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Όλες</SelectItem>
            <SelectItem value="GREEN">OK</SelectItem>
            <SelectItem value="YELLOW">Εκκρεμεί</SelectItem>
            <SelectItem value="RED">Πρόβλημα</SelectItem>
            <SelectItem value="UNKNOWN">Άγνωστο</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Syncs table */}
      <Card className="rounded-2xl border-border shadow-lifted">
        {syncsLoading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : (
          <Table className="text-[13px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Πελάτης</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">ΑΦΜ</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-right">Εκδοθέντα</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-right">Ληφθέντα</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-right">Χαρακτηρισμοί</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-right">Απορρίψεις</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider">Τελ. ενημέρωση</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {syncs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Δεν υπάρχουν εγγραφές myDATA.
                    Ενεργοποιήστε τον συγχρονισμό από το προφίλ πελάτη.
                  </TableCell>
                </TableRow>
              ) : (
                syncs.map((sync) => (
                  <TableRow key={sync.id} className="group table-row-hover">
                    <TableCell>
                      <StatusIcon status={sync.overallStatus} />
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to="/clients/$clientId"
                        params={{ clientId: sync.clientId }}
                        className="hover:underline"
                      >
                        {sync.clientBusinessName}
                      </Link>
                    </TableCell>
                    <TableCell className="font-data text-sm text-muted-foreground">
                      {sync.clientAfm}
                    </TableCell>
                    <TableCell className="text-right font-data">{sync.transmittedCount}</TableCell>
                    <TableCell className="text-right font-data">{sync.receivedCount}</TableCell>
                    <TableCell className="text-right">
                      {sync.pendingClassificationCount > 0 ? (
                        <Badge variant="warning" className="text-xs rounded-full font-data">
                          {sync.pendingClassificationCount}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {sync.rejectedCount > 0 ? (
                        <Badge variant="destructive" className="text-xs rounded-full font-data">
                          {sync.rejectedCount}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {sync.lastPolledAt
                        ? new Date(sync.lastPolledAt).toLocaleString('el-GR', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Συγχρονισμός τώρα"
                          disabled={triggerSync.isPending}
                          onClick={() => triggerSync.mutate(sync.clientId)}
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${triggerSync.isPending ? 'animate-spin' : ''}`} />
                        </Button>
                        <Link
                          to="/clients/$clientId"
                          params={{ clientId: sync.clientId }}
                        >
                          <Button variant="ghost" size="icon" className="h-7 w-7">
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="px-4 py-3 border-t">
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </Card>

      {/* Credentials Dialog */}
      {credDialogOpen && (
        <CredentialsDialog onClose={() => setCredDialogOpen(false)} />
      )}
    </div>
  );
}

// ─── Summary Card ────────────────────────────────────────

function SummaryCard({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: string;
  color: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card className="relative overflow-hidden border border-border rounded-[13px] bg-surface-2 hover-lift group">
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wider font-data text-muted-foreground mb-1">{label}</p>
        <p className={`text-3xl font-bold leading-tight font-data ${color}`}>{value}</p>
        <Icon className={`absolute right-4 bottom-3 h-10 w-10 ${color} opacity-[0.05] group-hover:opacity-[0.10] transition-opacity`} />
      </CardContent>
    </Card>
  );
}

// ─── Credentials Dialog ──────────────────────────────────

function CredentialsDialog({ onClose }: { onClose: () => void }) {
  const setCredentials = useSetMyDataCredentials();
  const testConnection = useTestMyDataConnection();
  const [form, setForm] = useState({ userId: '', subscriptionKey: '' });
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await setCredentials.mutateAsync(form);
    onClose();
  };

  const handleTest = async () => {
    setTestResult(null);
    const result = await testConnection.mutateAsync(form);
    setTestResult(result);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">Διαπιστευτήρια myDATA</DialogTitle>
          <DialogDescription>
            Εισάγετε τα στοιχεία σύνδεσης ΑΑΔΕ myDATA. Αποθηκεύονται κρυπτογραφημένα.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold text-muted-foreground">User ID (aade-user-id)</Label>
            <Input
              value={form.userId}
              onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}
              placeholder="Από τις ρυθμίσεις ΑΑΔΕ..."
              required
              className="rounded-[10px] bg-surface-2"
            />
          </div>
          <div>
            <Label className="text-xs font-bold text-muted-foreground">Subscription Key</Label>
            <Input
              type="password"
              value={form.subscriptionKey}
              onChange={(e) => setForm((f) => ({ ...f, subscriptionKey: e.target.value }))}
              placeholder="Ocp-Apim-Subscription-Key..."
              required
              className="rounded-[10px] bg-surface-2"
            />
          </div>

          {testResult && (
            <div className={`p-3 rounded-md text-sm ${testResult.success ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
              {testResult.success
                ? 'Η σύνδεση επιτυχής!'
                : `Αποτυχία: ${testResult.error}`}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleTest}
              disabled={testConnection.isPending || !form.userId || !form.subscriptionKey}
            >
              {testConnection.isPending ? 'Δοκιμή...' : 'Δοκιμή σύνδεσης'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Ακύρωση</Button>
            <Button type="submit" disabled={setCredentials.isPending}>
              {setCredentials.isPending ? 'Αποθήκευση...' : 'Αποθήκευση'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
